import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

/**
 * Hook for managing employees data (Admin view)
 * @param {string} businessId - Current business ID
 */
export const useEmployees = (businessId) => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!businessId) {
      setEmployees([]);
      setDepartments([]);
      setDesignations([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const [empRes, deptRes, desgRes] = await Promise.all([
        api.get(`/employees?business_id=${businessId}`),
        api.get(`/departments?business_id=${businessId}`),
        api.get(`/designations?business_id=${businessId}`)
      ]);
      
      let emps = empRes.data;
      setDepartments(deptRes.data);
      setDesignations(desgRes.data);
      
      // Auto-backfill employee codes if any missing
      if (emps.some(e => !e.employee_code)) {
        try {
          await api.post(`/employees/backfill-codes?business_id=${businessId}`);
          const refreshed = await api.get(`/employees?business_id=${businessId}`);
          emps = refreshed.data;
        } catch { /* silent */ }
      }
      setEmployees(emps);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch employees');
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Employee CRUD
  const createEmployee = async (employeeData) => {
    const res = await api.post('/employees', { ...employeeData, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const updateEmployee = async (employeeId, updates) => {
    const res = await api.put(`/employees/${employeeId}`, { ...updates, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const deleteEmployee = async (employeeId) => {
    await api.delete(`/employees/${employeeId}`);
    await fetchData();
  };

  // Department CRUD
  const createDepartment = async (deptData) => {
    const res = await api.post('/departments', { ...deptData, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const updateDepartment = async (deptId, updates) => {
    const res = await api.put(`/departments/${deptId}`, { ...updates, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const deleteDepartment = async (deptId) => {
    await api.delete(`/departments/${deptId}`);
    await fetchData();
  };

  // Designation CRUD
  const createDesignation = async (desgData) => {
    const res = await api.post('/designations', { ...desgData, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const updateDesignation = async (desgId, updates) => {
    const res = await api.put(`/designations/${desgId}`, { ...updates, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const deleteDesignation = async (desgId) => {
    await api.delete(`/designations/${desgId}`);
    await fetchData();
  };

  // Import/Export
  const importCSV = async (formData) => {
    const res = await api.post(`/employees/import/csv?business_id=${businessId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    await fetchData();
    return res.data;
  };

  const exportCSV = async () => {
    const res = await api.get(`/employees/export/csv?business_id=${businessId}`, { responseType: 'blob' });
    return res.data;
  };

  const exportEmployees = async (format = 'excel', departmentId = null, status = null) => {
    let url = `/employees/export/${format}?business_id=${businessId}`;
    if (departmentId) url += `&department_id=${departmentId}`;
    if (status) url += `&status=${status}`;
    const res = await api.get(url, { responseType: 'blob' });
    return res.data;
  };

  const downloadTemplate = async () => {
    const res = await api.get('/employees/template/csv', { responseType: 'blob' });
    return res.data;
  };

  // Employee Login Management
  const createEmployeeLogin = async (employeeId, password, phone) => {
    const res = await api.post(`/employees/${employeeId}/create-login`, { password, phone });
    await fetchData();
    return res.data;
  };

  const resetEmployeePassword = async (userId, newPassword) => {
    const res = await api.post('/auth/admin-reset-password', { user_id: userId, new_password: newPassword });
    return res.data;
  };

  // Helper functions
  const getDepartmentName = (id) => departments.find(d => d.id === id)?.name || "-";
  const getDesignationName = (id) => designations.find(d => d.id === id)?.name || "-";

  // Stats
  const stats = {
    total: employees.length,
    active: employees.filter(e => e.is_active !== false).length,
    departments: departments.length,
    designations: designations.length,
  };

  return {
    employees,
    departments,
    designations,
    loading,
    error,
    stats,
    fetchData,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    createDesignation,
    updateDesignation,
    deleteDesignation,
    importCSV,
    exportCSV,
    exportEmployees,
    downloadTemplate,
    createEmployeeLogin,
    resetEmployeePassword,
    getDepartmentName,
    getDesignationName,
  };
};

/**
 * Hook for managing departments (standalone)
 */
export const useDepartments = (businessId) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDepartments = useCallback(async () => {
    if (!businessId) {
      setDepartments([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/departments?business_id=${businessId}`);
      setDepartments(res.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const createDepartment = async (name, description = '') => {
    const res = await api.post('/departments', { name, description, business_id: businessId });
    setDepartments(prev => [...prev, res.data]);
    return res.data;
  };

  const updateDepartment = async (deptId, name, description = '') => {
    const res = await api.put(`/departments/${deptId}`, { name, description, business_id: businessId });
    setDepartments(prev => prev.map(d => d.id === deptId ? res.data : d));
    return res.data;
  };

  const deleteDepartment = async (deptId) => {
    await api.delete(`/departments/${deptId}`);
    setDepartments(prev => prev.filter(d => d.id !== deptId));
  };

  return { departments, loading, fetchDepartments, createDepartment, updateDepartment, deleteDepartment };
};

/**
 * Hook for managing designations (standalone)
 */
export const useDesignations = (businessId) => {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDesignations = useCallback(async () => {
    if (!businessId) {
      setDesignations([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/designations?business_id=${businessId}`);
      setDesignations(res.data);
    } catch (err) {
      console.error('Error fetching designations:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchDesignations();
  }, [fetchDesignations]);

  const createDesignation = async (name) => {
    const res = await api.post('/designations', { name, business_id: businessId });
    setDesignations(prev => [...prev, res.data]);
    return res.data;
  };

  const updateDesignation = async (desgId, name) => {
    const res = await api.put(`/designations/${desgId}`, { name, business_id: businessId });
    setDesignations(prev => prev.map(d => d.id === desgId ? res.data : d));
    return res.data;
  };

  const deleteDesignation = async (desgId) => {
    await api.delete(`/designations/${desgId}`);
    setDesignations(prev => prev.filter(d => d.id !== desgId));
  };

  return { designations, loading, fetchDesignations, createDesignation, updateDesignation, deleteDesignation };
};
